import React, { JSX, useState } from "react";
import styled, { css } from "styled-components";
import { ImageVersions } from "@domain/media/dtos";
import { Media } from "@domain/media/media";
import { mobile } from "@ui/styles/utils";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { row, stack } from "@ui/components/layout";
import { Gallery } from "./gallery";
import { ImageView, ZoomedImageView } from "./imageView";

interface MosaicViewProps extends React.HTMLAttributes<HTMLDivElement> {
	images: Media[];
}
export const MosaicView: React.FC<MosaicViewProps> = ({ images, ...htmlProps }) => {
	const [moreOpened, setMoreOpened] = useState(false);
	const extraCount = images.length > 5 ? images.length - 5 : 0;

	const getImageWithPlaceholder = (index: number, fixed?: boolean): JSX.Element => {
		const realIndex = !fixed && images.length > 4 ? index + 1 : index;
		return (
			<StyledImageView
				cover
				key={images[realIndex]?.id}
				image={images[realIndex]}
				imageStyle={imageStyle}
				gallery={images}
				imageVersion={ImageVersions.MEDIUM}
			/>
		);
	};

	return (
		<>
			<Container {...htmlProps}>
				{images.length === 2 ? (
					<Mosaic rowDirection>{images.map((_, index) => getImageWithPlaceholder(index))}</Mosaic>
				) : (
					<Mosaic>
						<Top>
							{getImageWithPlaceholder(0, true)}
							{images.length > 4 && getImageWithPlaceholder(1, true)}
						</Top>
						<Bottom>
							{getImageWithPlaceholder(1)}
							{getImageWithPlaceholder(2)}
							{images.length > 3 && (
								<LastImage>
									{getImageWithPlaceholder(3)}
									{extraCount > 0 && <ExtraCounter onClick={() => setMoreOpened(true)}>+{extraCount}</ExtraCounter>}
								</LastImage>
							)}
						</Bottom>
					</Mosaic>
				)}
				{moreOpened && (
					<Gallery
						onClose={() => setMoreOpened(false)}
						items={images}
						initialIndex={4}
						renderItem={(img) => (
							<ZoomedImageView fullSize image={img} imageVersion={ImageVersions.LARGE} zoomDisabled />
						)}
					/>
				)}
			</Container>
		</>
	);
};

const Container = styled(RoundedBlock)`
	overflow: hidden;
`;

const Mosaic = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "rowDirection",
})<{ rowDirection?: boolean }>`
	${({ rowDirection }) => (rowDirection ? row(2, "flex-start", "stretch") : stack(2, "flex-start", "stretch"))};
	width: 100%;
	height: 100%;
`;

const Top = styled.div`
	${row(2)};
	height: 60%;
	overflow: hidden;
`;

const Bottom = styled.div`
	${row(2)};
	height: 40%;
`;

const imageStyle = css`
	height: 100%;
	object-fit: cover;
	visibility: visible !important;
`;

const StyledImageView = styled(ImageView)`
	flex: 1 1 auto;
	height: 100%;
`;

const LastImage = styled.div`
	position: relative;
	min-height: 100%;
	max-height: 100%;
	min-width: 150px;
	max-width: 300px;
	@media ${mobile} {
		min-width: 50px;
		max-width: 120px;
	}
`;

const ExtraCounter = styled.div`
	cursor: zoom-in;
	${({ theme }) => theme.textStyles.title};
	color: ${({ theme }) => theme.colors.white};
	background-color: rgba(0, 0, 0, 0.7);
	position: absolute;
	top: 0;
	bottom: 0;
	right: 0;
	left: 0;
	${row(0, "center", "center")};
`;
